/** The word that spiked each month: a tile per month pops in, one after another. */

import { scaleStep } from "../../sound";
import { Fit, Scene, Zone, type BlobSpec } from "../../story/stage";
import { useTimeline } from "../../story/timeline";
import type { Chat } from "../../types";

const BLOBS: BlobSpec[] = [{ colour: "teal", x: 60, y: 1820, r: 260, seed: 28.2, wob: 0.18 }];

const GRID_TOP = 380;
const TILE = 180;
const GAP = 22;

/** "NOV '25" on the first tile and each January, just "DEC" otherwise. */
function monthLabel(month: string, first: boolean) {
  const [y, m] = month.split("-").map(Number);
  // en-US, so September reads "SEP" like the video rather than "SEPT".
  const name = new Date(y, m - 1, 1).toLocaleDateString("en-US", { month: "short" }).toUpperCase();
  return first || m === 1 ? `${name} '${String(y).slice(2)}` : name;
}

/**
 * The video lights up the month a word "belonged to". The only claim the data
 * backs is a word that topped more than one month, so that is what lights up,
 * and only when one word clearly did it most.
 */
function repeatWinner(months: Chat["word_of_month"]) {
  const counts = new Map<string, number>();
  for (const m of months) if (m.word) counts.set(m.word, (counts.get(m.word) ?? 0) + 1);
  const most = Math.max(0, ...counts.values());
  const top = [...counts].filter(([, n]) => n === most);
  return most > 1 && top.length === 1 ? { word: top[0][0], months: most } : null;
}

export function WordOfMonth({ months }: { months: Chat["word_of_month"] }) {
  const winner = repeatWinner(months);
  const rows = Math.ceil(months.length / 3);
  const lastLands = 0.6 + (months.length - 1) * 0.16 + 0.45;

  const root = useTimeline((s) => {
    s.rise(s.q("[data-part=head]"), 0.35, { y: 30, stagger: 0.12 });
    const tiles = s.q(".wtile");
    s.pop(tiles, 0.6, { from: 0.4, stagger: 0.16, dur: 0.45, sound: false });
    // A note per tile, up the scale, so the year plays as a little tune.
    tiles.forEach((_, i) => s.sound("pop", 0.6 + i * 0.16 + 0.05, scaleStep(i % 10)));
    if (!winner) return;
    const at = Math.max(3.2, lastLands + 0.15);
    s.tl.to(s.q(".wtile[data-hot]"), { scale: 1.08, duration: 0.3, yoyo: true, repeat: 1, ease: "sine.inOut" }, at);
    s.tl.fromTo(s.q("[data-part=glow]"), { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.3, yoyo: true, repeat: 1, ease: "sine.inOut" }, at);
    s.sound("ding", at);
    s.rise("[data-part=foot]", at + 0.1, { y: 24, sound: false });
  });

  return (
    <div ref={root} className="contents">
      <Scene blobs={BLOBS}>
        <Zone at="top">
          <div data-part="head" className="kick">
            Word of the month
          </div>
          <div data-part="head" className="lead">
            The word that spiked each month
          </div>
        </Zone>
        <Zone at="middle">
          <div className="wgrid" style={{ top: GRID_TOP }}>
            {months.map((m, i) => {
              const hot = !!winner && m.word === winner.word;
              return (
                <div key={m.month} data-testid="month" data-hot={hot || undefined} className="wtile" style={{ position: "relative" }}>
                  {/* The highlight flashes in and out, so the still frame never shows it. */}
                  {hot ? (
                    <div
                      data-part="glow"
                      className="wr-hide"
                      style={{ position: "absolute", inset: 0, borderRadius: "inherit", background: "var(--wr-fuchsia)" }}
                    />
                  ) : null}
                  <div className="mono" style={{ position: "relative" }}>
                    {monthLabel(m.month, i === 0)}
                  </div>
                  {m.word ? (
                    <Fit size={64} min={28} className="disp" style={{ position: "relative" }}>
                      {m.word}
                    </Fit>
                  ) : (
                    <div className="disp" style={{ color: "var(--wr-muted)" }}>
                      —
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Zone>
        <Zone at="bottom">
          {winner ? (
            <div data-part="foot" className="line" style={{ top: GRID_TOP + rows * TILE + (rows - 1) * GAP + 64 }}>
              “{winner.word}” was the word of {winner.months} months.
            </div>
          ) : null}
        </Zone>
      </Scene>
    </div>
  );
}
